import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useToasts } from 'react-toast-notifications';
import { ValueType, OptionsType } from 'react-select';

import Select from './select';

import Loader from '../loader';

import MapController from '../../../controllers/MapController';

import { IOptionsType } from '../../../types/react-select-options';

import { RootState } from '../../../reducers/app';
import { toggleMapLoader } from '../../../reducers/mapview/actions';
import {
  setSelectedMergeIntersectLayers,
  setSelectedMergeIntersectOperation,
} from '../../../reducers/auditdata/actions';

import './merge-intersect.scss';
/**
 * Component enabling a user to select an Audit area
 */

const MergeIntersect = () => {
  // LOCAL STATE

  // REDUX
  const loaderActive = useSelector((state: RootState) => state.leftpanel.loader);
  const myLayers = useSelector((state: RootState) => state.mapview.myLayers);
  const auditArea = useSelector((state: RootState) => state.auditdata.auditArea.graphic);
  const selectedLayers = useSelector((state: RootState) => state.auditdata.selectedMergeIntersectLayers);
  const selectedOperation = useSelector((state: RootState) => state.auditdata.selectedMergeIntersectOperation);
  const dispatch = useDispatch();

  const { addToast } = useToasts();

  // this function checks if our selected option matches the type signature for a React Select value
  const isOptionsType = (layers: ValueType<IOptionsType>): layers is OptionsType<IOptionsType> => {
    return layers ? true : false;
  };

  const intersectLayers = () => {
    const ids = isOptionsType(selectedLayers) ? selectedLayers.map((l) => l.value) : [selectedLayers?.value];

    MapController.intersectLayers(ids)
      .then((res: string) => {
        dispatch(setSelectedMergeIntersectLayers([]));
      })
      .catch((res: string) => {
        addToast(res, { appearance: 'error', autoDismiss: true });
        dispatch(toggleMapLoader(false));
      });
  };

  const mergeLayers = () => {
    const ids = isOptionsType(selectedLayers) ? selectedLayers.map((l) => l.value) : [selectedLayers?.value];

    MapController.mergeLayers(ids)
      .then((res: string) => {
        dispatch(setSelectedMergeIntersectLayers([]));
      })
      .catch((res: string) => {
        addToast(res, { appearance: 'error', autoDismiss: true });
        dispatch(toggleMapLoader(false));
      });
  };

  const createLayerOptions = () => {
    const options: Array<IOptionsType> = [];
    myLayers.forEach((layer) => {
      if (!layer.id.includes('raster-analysis') && layer.id !== 'auditAreaLayer') {
        options.push({ value: layer.id, label: layer.title });
      }
    });
    // ADD LOGIC FOR FEATURED LAYERS HERE

    MapController.mapLayers.forEach((layer) => {
      if (layer.includeInDropdowns) {
        options.push({ value: layer.id, label: layer.title });
      }
    });

    return options.sort((a, b) => (a.label.toLowerCase() > b.label.toLowerCase() ? 1 : -1));
  };

  const disabled = () => {
    if (!auditArea) {
      return true;
    }
    return isOptionsType(selectedLayers) ? selectedLayers?.length < 1 : true;
  };

  return (
    <div className='merge-intersect'>
      {loaderActive && <Loader />}
      <div className='merge-intersect__selector'>
        <p className='merge-intersect__selector-title'>Combine Relevant Layers</p>
        <div className='merge-intersect__selector-main'>
          <p>Select an FSC Certified Forest from the map or draw or upload your Area of Interest</p>
          <Select
            isMulti
            options={createLayerOptions()}
            value={selectedLayers}
            onChange={(e: ValueType<IOptionsType>) => dispatch(setSelectedMergeIntersectLayers(e))}
            isDisabled={false}
            placeholder='Select Relevant Layers'
          />
          <div className='merge-intersect__radio-group'>
            <div className='radio-option'>
              <input
                type='radio'
                id='merge'
                name='option'
                value='merge'
                checked={selectedOperation === 'merge'}
                onChange={(e) => dispatch(setSelectedMergeIntersectOperation(e.target.value))}
              />
              <label htmlFor='merge'>Merge</label>
            </div>
            <div className='radio-option'>
              <input
                type='radio'
                id='intersection'
                name='option'
                value='intersection'
                checked={selectedOperation === 'intersection'}
                onChange={(e) => dispatch(setSelectedMergeIntersectOperation(e.target.value))}
              />
              <label htmlFor='intersection'>Intersection</label>
            </div>
          </div>
          <div className='merge-intersect__process'>
            <button
              className='merge-intersect__process-button'
              onClick={() => (selectedOperation === 'merge' ? mergeLayers() : intersectLayers())}
              disabled={disabled()}
            >
              Process Operation
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MergeIntersect;
